import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { cloneDeep } from 'lodash';

import { FloatingField, FloatingFieldRepeatEnum, Locale, PositionElementEnum, SignatureFormat, Whitelabel } from '@app/models';
import { CertificateService, DocumentService, WhitelabelService } from '@app/services';
import { RepeatFloatingFieldModalService } from './repeat-floating-field-modal/repeat-floating-field-modal.service';
import { I18nService } from '@app/core';

interface ParsedFloatingField {
  originalField: FloatingField;
  element: PositionElementEnum;
  borderType: FloatingFieldBorderType;
  signature?: FloatingField['signature'];
  isSelected?: boolean;
  canRotate?: boolean;
  angle?: number;
  idColor?: string;
  styles: { top: string; left: string; height: string; lineHeight: string; width: string; fontSize: string; transform: string; transformOrigin: string };
  innerStyles: {
    outlineColor?: string;
    backgroundColor?: string;
    transform: string;
    transformOrigin: string;
    '--field-outline-width': string;
    '--field-selected-outline-width': string;
    '--field-border-radius': string;
    '--field-inverse-scale': string;
    '--field-text-inverse-scale': string;
    '--field-rotate-top': string;
    '--field-rotate-left': string;
    '--field-options-top': string;
    '--field-options-right': string;
  };
}

type ResizeCorner = 'left_top' | 'left_bottom' | 'right_top' | 'right_bottom';

interface ResizeState {
  field: ParsedFloatingField;
  page: HTMLElement;
  pageRect: DOMRect;
  baseWidth: number;
  baseHeight: number;
  cornerX: number;
  cornerY: number;
  anchorX: number;
  anchorY: number;
  directionX: number;
  directionY: number;
  startProjection: number;
  startScale: number;
}

enum FloatingFieldBorderType {
  Advanced = 'advanced',
  NationalCertificate = 'icp',
  InternationalCertificate = 'certificate'
}

@Component({
  selector: 'app-floating-fields',
  templateUrl: './floating-fields.component.html',
  styleUrls: ['./floating-fields.component.scss'],
  standalone: false
})
export class FloatingFieldsComponent implements OnChanges {
  @Input() pdfContainer!: HTMLElement;
  @Input() fields: FloatingField[] = [];
  @Input() scale: number;
  @Input() pagesCount: number;
  @Input() enableEdit: boolean;
  @Input() qualified: boolean;
  @Input() isNewSignatureStyle: boolean;
  @Input() documentWhitelabel: Whitelabel;
  @Input() documentLocale: Locale;
  @Input() isDocumentWhitelabelLoaded: boolean;
  @Output() selectChange = new EventEmitter<FloatingField>();
  parsedFields: ParsedFloatingField[] = [];
  isDraggingField: boolean;
  isRotateField: boolean;
  isResizingField: boolean;

  originShiftX: number;
  originShiftY: number;
  clickedPointShiftX: number;
  clickedPointShiftY: number;
  elementRotate: number;
  dateFormat: string;
  timezone: string;
  language: Locale['language'];

  readonly SignatureFormat = SignatureFormat;
  readonly PositionElementEnum = PositionElementEnum;
  readonly FloatingFieldBorderType = FloatingFieldBorderType;
  private lastDragEvent: MouseEvent | TouchEvent;
  private currentDraggingField: ParsedFloatingField;
  private resizeState: ResizeState;
  private readonly minFieldScale = 0.5;
  private readonly maxFieldScale = 4;
  private readonly fieldSizes = { width: 160, fontSize: 10, fullHeight: 60, partialHeight: 36 };
  private readonly fieldSizesNewSignatureStyle = { width: 210, fontSize: 8, fullHeight: 70, partialHeight: 36 };

  constructor(
    public documentService: DocumentService,
    public whitelabelService: WhitelabelService,
    private certificateService: CertificateService,
    private repeatFloatingFieldModalService: RepeatFloatingFieldModalService,
    private i18nService: I18nService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fields && changes.fields.currentValue !== changes.fields.previousValue) {
      this.refreshFields(changes.fields.currentValue);
    }
    if ((changes.qualified || changes.isNewSignatureStyle) && this.fields.length > 0) {
      this.refreshFields(this.fields);
    }
    if (changes.documentLocale) {
      this.dateFormat = this.i18nService.dateFormatAsMask(this.documentLocale?.date_format) || 'shortDate';
      this.timezone = this.i18nService.getTimezoneOffset(this.documentLocale?.timezone) || null;
      this.language = this.documentLocale?.language || null;
    }
  }

  createField(field: FloatingField, centerFieldPosition?: boolean) {
    const newField = this.fixFieldToInsideBoundaries(centerFieldPosition ? this.fixFieldToCenterPosition(field) : field);
    this.fields.push(newField);
    this.onStopDraggingField();
    this.refreshFields(this.fields);
    return newField;
  }

  refreshFields(fields?: FloatingField[]) {
    const selectedField = this.parsedFields.find(f => f.isSelected);
    setTimeout(() => (this.parsedFields = this.parseFields(fields || this.fields, selectedField)));
  }

  removeField(field: FloatingField) {
    const fieldIndex = this.fields.indexOf(field);
    if (fieldIndex >= 0) {
      const parsedField = this.parsedFields.find(pField => pField.originalField === field);
      if (parsedField && parsedField.isSelected) {
        this.selectChange.emit(null);
      }

      const idLink = field.idLink;
      this.fields.filter(f => f === field || (idLink && idLink === f.idLink)).forEach(f => this.fields.splice(this.fields.indexOf(f), 1));
      this.refreshFields(this.fields);
    }
  }

  fieldIdentifier(index: number, field: ParsedFloatingField) {
    return field.originalField;
  }

  onStartDraggingField(event: MouseEvent | TouchEvent, parsedField: ParsedFloatingField) {
    if (this.isResizingField) {
      return;
    }

    this.lastDragEvent = event;
    this.currentDraggingField = parsedField;
    this.isDraggingField = this.enableEdit && !parsedField.signature;
  }

  onStopDraggingField() {
    this.lastDragEvent = null;
    this.currentDraggingField = null;
    this.isDraggingField = false;
  }

  onDragField(event: MouseEvent | TouchEvent) {
    let currentPage;
    const field = this.currentDraggingField;
    let shiftX = 'x' in event ? event.x : 'touches' in event ? event.touches[0].pageX : null;
    let shiftY = 'x' in event ? event.y : 'touches' in event ? event.touches[0].pageY : null;

    if (this.isResizingField) {
      this.resizeField(event);
      return;
    }

    if (this.isDraggingField) {
      document.body.style.overflow = 'hidden';
      if (document.getElementsByClassName('models position').length > 0) {
        // @ts-ignore
        document.getElementsByClassName('models position')[0].style.overflowY = 'hidden';
      }
      shiftX = shiftX - ('x' in this.lastDragEvent ? this.lastDragEvent.x : 'touches' in this.lastDragEvent ? this.lastDragEvent.touches[0].pageX : null);
      shiftY = shiftY - ('y' in this.lastDragEvent ? this.lastDragEvent.y : 'touches' in this.lastDragEvent ? this.lastDragEvent.touches[0].pageY : null);
      this.lastDragEvent = event;

      if (shiftX !== 0 || shiftY !== 0) {
        currentPage = this.currentPageFor(field.originalField);
        if (shiftX !== 0) {
          field.originalField.position.x = field.originalField.position.x + (shiftX * 100) / currentPage.offsetWidth;
        }
        if (shiftY !== 0) {
          field.originalField.position.y = field.originalField.position.y + (shiftY * 100) / currentPage.offsetHeight;
        }
        this.fixFieldToInsideBoundaries(field.originalField, currentPage);

        if (shiftX !== 0) {
          field.styles.left = this.calcFieldAbsoluteLeftPosition(field.originalField, currentPage) + '%';
        }
        if (shiftY !== 0) {
          field.styles.top = this.calcFieldAbsoluteTopPosition(field.originalField, currentPage) + '%';
        }

        if (field.originalField.idLink) {
          setTimeout(() => {
            this.fields
              .filter(f => f !== field.originalField && field.originalField.idLink === f.idLink)
              .forEach(f => {
                f.position.x = field.originalField.position.x;
                f.position.y = field.originalField.position.y;
              });
            this.parsedFields
              .filter(f => f !== field && field.originalField.idLink === f.originalField.idLink)
              .forEach(f => {
                const linkedPage = this.currentPageFor(f.originalField);
                f.styles.left = this.calcFieldAbsoluteLeftPosition(f.originalField, linkedPage) + '%';
                f.styles.top = this.calcFieldAbsoluteTopPosition(f.originalField, linkedPage) + '%';
              });
          });
        }
      }
    }

    if (this.isRotateField) {
      const pointer = this.pointerPosition(event);
      if (pointer && (pointer.clientX !== this.originShiftX || pointer.clientY !== this.originShiftY)) {
        let radius = Math.atan2(pointer.clientY - this.originShiftY, pointer.clientX - this.originShiftX);
        radius -= Math.atan2(this.clickedPointShiftY - this.originShiftY, this.clickedPointShiftX - this.originShiftX);
        radius += field.angle;

        const angle = (((radius * (360 / (2 * Math.PI))) % 360) + 360) % 360;
        field.styles.transform = this.shellTransform(this.currentPageFor(field.originalField));
        field.innerStyles.transform = this.innerTransform(field.originalField, angle || 0);
        field.originalField.position.angle = parseFloat(angle.toFixed(2));
        this.elementRotate = radius;

        if (field.originalField.idLink) {
          setTimeout(() => {
            this.fields
              .filter(f => f !== field.originalField && field.originalField.idLink === f.idLink)
              .forEach(f => {
                f.position.angle = field.originalField.position.angle;
              });

            this.parsedFields.filter(f => f !== field && field.originalField.idLink === f.originalField.idLink).forEach(f => this.applyParsedFieldStyles(f));
          });
        }
      }
    }
  }

  onStartRotateField(event: MouseEvent | TouchEvent, parsedField: ParsedFloatingField) {
    this.lastDragEvent = event;
    this.currentDraggingField = parsedField;
    this.currentDraggingField.canRotate = true;
    this.isRotateField = this.enableEdit;
    this.isDraggingField = false;

    const pointer = this.pointerPosition(event);
    const fieldElement = (event.currentTarget as HTMLElement).closest('.floating-field') as HTMLElement;
    const fieldRect = fieldElement.getBoundingClientRect();

    this.clickedPointShiftX = pointer.clientX;
    this.clickedPointShiftY = pointer.clientY;
    this.elementRotate = this.currentDraggingField.angle || 0;

    event.preventDefault();
    event.stopPropagation();

    this.originShiftX = fieldRect.left + fieldRect.width / 2;
    this.originShiftY = fieldRect.top + fieldRect.height / 2;

    this.currentDraggingField.angle = this.currentDraggingField.angle || 0;
  }

  onStartResizeField(event: MouseEvent | TouchEvent, parsedField: ParsedFloatingField, corner: ResizeCorner) {
    if (!this.enableEdit || parsedField.signature) {
      return;
    }

    this.lastDragEvent = event;
    this.currentDraggingField = parsedField;
    this.isResizingField = true;
    this.isDraggingField = false;
    this.isRotateField = false;
    this.resizeState = this.createResizeState(event, parsedField, corner);
    this.selectField(parsedField.originalField);

    event.preventDefault();
    event.stopPropagation();
  }

  onStopResizeField() {
    this.isResizingField = false;
    this.resizeState = null;
  }

  onStopRotateField() {
    if (this.currentDraggingField && this.isRotateField) {
      this.currentDraggingField.angle = this.elementRotate || 0;
      this.currentDraggingField.canRotate = false;
    }

    this.elementRotate = null;
    this.isRotateField = false;
  }

  onStopSelectedField() {
    this.onStopRotateField();
    this.onStopDraggingField();
    this.onStopResizeField();

    if (this.enableEdit) {
      document.body.style.overflow = 'unset';
      if (document.getElementsByClassName('models position').length > 0) {
        // @ts-ignore
        document.getElementsByClassName('models position')[0].style.overflowY = 'auto';
      }
    }
  }

  selectField(field: FloatingField) {
    if (this.enableEdit) {
      if (!field && this.isResizingField) {
        return;
      }

      this.parsedFields.forEach(f => (f.isSelected = !!field && (field === f.originalField || (!!field.idLink && f.originalField.idLink === field.idLink))));
      this.selectChange.emit(field);
    }
  }

  openRepeatFieldModal(field: FloatingField) {
    this.repeatFloatingFieldModalService.open().subscribe({
      next: option => {
        field.idLink = Math.floor(Math.random() * 1000000000);
        const pagesAdjust = option === FloatingFieldRepeatEnum.ALL_PAGES_BUT_LAST ? -1 : 0;
        for (let i = 0; i < this.pagesCount + pagesAdjust; i++) {
          if (field.position.z !== i + 1) {
            const newField = cloneDeep(field);
            newField.position.z = i + 1;
            newField.idLink = field.idLink;
            this.createField(newField, false);
          }
        }
      }
    });
  }

  unlinkFields(field: FloatingField) {
    this.fields.filter(f => f.idLink && f.idLink === field.idLink).forEach(f => (f.idLink = null));
  }

  unlinkField(parsedField: ParsedFloatingField) {
    if (parsedField.originalField.idLink) {
      parsedField.originalField.idLink = null;
    }
  }

  currentFormat(parsedField: ParsedFloatingField) {
    return parsedField.signature[parsedField.element && parsedField.element === PositionElementEnum.Initials ? 'initialsFormat' : 'format'];
  }

  returnPlacementTooltip(parsedField: ParsedFloatingField) {
    const angle = (((parsedField.angle * (360 / (2 * Math.PI))) % 360) + 360) % 360; // convert angle
    return angle >= 45 && angle < 135 ? 'right' : angle >= 135 && angle < 225 ? 'bottom' : angle >= 225 && angle < 315 ? 'left' : 'top';
  }

  returnFieldWidth(element: PositionElementEnum) {
    return element === PositionElementEnum.Signature && this.isNewSignatureStyle ? this.fieldSizesNewSignatureStyle.width : this.fieldSizes.width;
  }

  returnFieldFullHeight(element: PositionElementEnum) {
    return element === PositionElementEnum.Signature && this.isNewSignatureStyle ? this.fieldSizesNewSignatureStyle.fullHeight : this.fieldSizes.fullHeight;
  }

  returnFieldFontSize(element: PositionElementEnum) {
    return element === PositionElementEnum.Signature && this.isNewSignatureStyle ? this.fieldSizesNewSignatureStyle.fontSize : this.fieldSizes.fontSize;
  }

  returnFieldLineHeight(element: PositionElementEnum) {
    return [PositionElementEnum.Name, PositionElementEnum.Cpf, PositionElementEnum.Date].includes(element) ? 'normal' : this.fieldHeight(element) + 'px';
  }

  private resizeField(event: MouseEvent | TouchEvent) {
    if (!this.resizeState) {
      return;
    }

    const pointer = this.pointerPosition(event);
    if (!pointer) {
      return;
    }

    const state = this.resizeState;
    const projection = (pointer.clientX - state.anchorX) * state.directionX + (pointer.clientY - state.anchorY) * state.directionY;
    const nextScale = this.clamp(state.startScale * (projection / state.startProjection), this.minFieldScale, this.maxFieldScale);

    this.applyResizedFieldPosition(state, parseFloat(nextScale.toFixed(4)));
    this.syncLinkedResizeFields(state.field);

    event.preventDefault();
  }

  private createResizeState(event: MouseEvent | TouchEvent, field: ParsedFloatingField, corner: ResizeCorner): ResizeState {
    const pointer = this.pointerPosition(event);
    const page = this.currentPageFor(field.originalField);
    const pageRect = page.getBoundingClientRect();
    const cornerPosition = this.resizeCornerPosition(corner);
    const baseWidth = this.fieldPageWidth(field.element, page);
    const baseHeight = this.fieldPageHeight(field.element, page);
    const width = baseWidth * this.fieldScale(field.originalField);
    const height = baseHeight * this.fieldScale(field.originalField);
    const originX = pageRect.left + (field.originalField.position.x * pageRect.width) / 100;
    const originY = pageRect.top + (field.originalField.position.y * pageRect.height) / 100;
    const centerX = originX + baseWidth / 2;
    const centerY = originY + baseHeight / 2;
    const angle = this.degreesToRadians(field.originalField.position.angle || 0);
    const anchorLocal = this.rotatePoint((-cornerPosition.x * width) / 2, (-cornerPosition.y * height) / 2, angle);
    const diagonal = this.rotatePoint(cornerPosition.x * width, cornerPosition.y * height, angle);
    const diagonalLength = Math.hypot(diagonal.x, diagonal.y) || 1;
    const anchorX = centerX + anchorLocal.x;
    const anchorY = centerY + anchorLocal.y;
    const directionX = diagonal.x / diagonalLength;
    const directionY = diagonal.y / diagonalLength;
    const startProjection = Math.max((pointer.clientX - anchorX) * directionX + (pointer.clientY - anchorY) * directionY, 1);

    return {
      field,
      page,
      pageRect,
      baseWidth,
      baseHeight,
      cornerX: cornerPosition.x,
      cornerY: cornerPosition.y,
      anchorX,
      anchorY,
      directionX,
      directionY,
      startProjection,
      startScale: this.fieldScale(field.originalField)
    };
  }

  private applyResizedFieldPosition(state: ResizeState, nextScale: number) {
    const field = state.field;
    const width = state.baseWidth * nextScale;
    const height = state.baseHeight * nextScale;
    const angle = this.degreesToRadians(field.originalField.position.angle || 0);
    const anchorLocal = this.rotatePoint((-state.cornerX * width) / 2, (-state.cornerY * height) / 2, angle);
    const centerX = state.anchorX - anchorLocal.x;
    const centerY = state.anchorY - anchorLocal.y;

    field.originalField.position.scale = nextScale;
    field.originalField.position.x = ((centerX - state.pageRect.left - state.baseWidth / 2) * 100) / state.pageRect.width;
    field.originalField.position.y = ((centerY - state.pageRect.top - state.baseHeight / 2) * 100) / state.pageRect.height;
    this.fixFieldToInsideBoundaries(field.originalField, state.page);
    this.applyParsedFieldStyles(field);
  }

  private syncLinkedResizeFields(parsedField: ParsedFloatingField) {
    const field = parsedField.originalField;
    if (!field.idLink) {
      return;
    }

    this.fields
      .filter(f => f !== field && f.idLink === field.idLink)
      .forEach(f => {
        f.position.x = field.position.x;
        f.position.y = field.position.y;
        f.position.scale = field.position.scale;
      });

    this.parsedFields.filter(f => f !== parsedField && f.originalField.idLink === field.idLink).forEach(f => this.applyParsedFieldStyles(f));
  }

  private applyParsedFieldStyles(field: ParsedFloatingField) {
    const currentPage = this.currentPageFor(field.originalField);
    field.styles.left = this.calcFieldAbsoluteLeftPosition(field.originalField, currentPage) + '%';
    field.styles.top = this.calcFieldAbsoluteTopPosition(field.originalField, currentPage) + '%';
    field.styles.transform = this.shellTransform(currentPage);
    field.innerStyles.transform = this.innerTransform(field.originalField);
    field.innerStyles['--field-inverse-scale'] = String(1 / this.fieldScale(field.originalField));
    field.innerStyles['--field-text-inverse-scale'] = String(1 / Math.max(1, this.fieldScale(field.originalField)));
    field.innerStyles['--field-rotate-top'] = `${-27 / this.fieldScale(field.originalField)}px`;
    field.innerStyles['--field-rotate-left'] = `${-12 / this.fieldScale(field.originalField)}px`;
    field.innerStyles['--field-options-top'] = `${-30 / this.fieldScale(field.originalField)}px`;
    field.innerStyles['--field-options-right'] = `${-2 / this.fieldScale(field.originalField)}px`;
    Object.assign(field.innerStyles, this.fieldOutlineStyles(field.originalField));
  }

  private resizeCornerPosition(corner: ResizeCorner) {
    return {
      left_top: { x: -1, y: -1 },
      left_bottom: { x: -1, y: 1 },
      right_top: { x: 1, y: -1 },
      right_bottom: { x: 1, y: 1 }
    }[corner];
  }

  private pointerPosition(event: MouseEvent | TouchEvent) {
    if ('touches' in event) {
      const touch = event.touches[0] || event.changedTouches[0];
      return touch ? { clientX: touch.clientX, clientY: touch.clientY } : null;
    }

    return { clientX: event.clientX, clientY: event.clientY };
  }

  private rotatePoint(x: number, y: number, angle: number) {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return { x: cos * x - sin * y, y: sin * x + cos * y };
  }

  private degreesToRadians(angle: number) {
    return (angle * Math.PI) / 180;
  }

  private fieldScale(field: FloatingField) {
    const scale = Number(field.position.scale);
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
  }

  private pageRenderScale(page?: HTMLElement) {
    const pageScale = Number(page?.dataset?.floatingFieldsScale);
    if (Number.isFinite(pageScale) && pageScale > 0) {
      return pageScale;
    }

    return this.scale || 1;
  }

  private fieldPageWidth(element: PositionElementEnum, page?: HTMLElement) {
    return this.returnFieldWidth(element) * this.pageRenderScale(page);
  }

  private fieldPageHeight(element: PositionElementEnum, page?: HTMLElement) {
    return this.fieldHeight(element) * this.pageRenderScale(page);
  }

  private renderedFieldWidth(field: FloatingField, page?: HTMLElement) {
    return this.fieldPageWidth(field.element, page) * this.fieldScale(field);
  }

  private renderedFieldHeight(field: FloatingField, page?: HTMLElement) {
    return this.fieldPageHeight(field.element, page) * this.fieldScale(field);
  }

  private fieldRenderScale(field: FloatingField) {
    return this.fieldScale(field);
  }

  private shellTransform(page?: HTMLElement) {
    return `scale(${this.pageRenderScale(page)})`;
  }

  private innerTransform(field: FloatingField, angle = field.position.angle || 0) {
    return `rotate(${angle}deg) scale(${this.fieldRenderScale(field)})`;
  }

  private fieldOutlineStyles(field: FloatingField) {
    const scale = this.fieldScale(field);

    return {
      ...(field.idColor ? { outlineColor: field.idColor, backgroundColor: `${field.idColor}22` } : {}),
      '--field-outline-width': `${1 / scale}px`,
      '--field-selected-outline-width': `${2 / scale}px`,
      '--field-border-radius': `${5 / scale}px`
    };
  }

  private fieldControlStyles(field: FloatingField) {
    const scale = this.fieldScale(field);

    return {
      '--field-inverse-scale': String(1 / scale),
      '--field-text-inverse-scale': String(1 / Math.max(1, scale)),
      '--field-rotate-top': `${-27 / scale}px`,
      '--field-rotate-left': `${-12 / scale}px`,
      '--field-options-top': `${-30 / scale}px`,
      '--field-options-right': `${-2 / scale}px`
    };
  }

  private clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max);
  }

  private parseFields(fields: FloatingField[], selectedField?: ParsedFloatingField) {
    return fields
      .filter(field => this.pdfContainer.childElementCount >= field.position.z)
      .map(field => {
        const currentPage = this.currentPageFor(field);
        field.position.angle = field.position.angle || 0;
        return {
          originalField: field,
          element: field.element,
          borderType: this.getBorderType(field),
          idColor: field.idColor,
          signature: field.signature || null,
          isSelected: !!selectedField && (selectedField.originalField === field || (!!selectedField.originalField.idLink && selectedField.originalField.idLink === field.idLink)),
          angle: (((field.position.angle / (360 / (2 * Math.PI))) % 360) + 360) % 360,
          styles: {
            width: this.returnFieldWidth(field.element) + 'px',
            height: this.fieldHeight(field.element) + 'px',
            lineHeight: this.returnFieldLineHeight(field.element),
            fontSize: this.returnFieldFontSize(field.element) + 'px',
            left: this.calcFieldAbsoluteLeftPosition(field, currentPage) + '%',
            top: this.calcFieldAbsoluteTopPosition(field, currentPage) + '%',
            transform: this.shellTransform(currentPage),
            transformOrigin: 'top left'
          },
          innerStyles: {
            transform: this.innerTransform(field),
            transformOrigin: 'center',
            ...this.fieldOutlineStyles(field),
            ...this.fieldControlStyles(field)
          }
        };
      });
  }

  private currentPageFor(field: FloatingField) {
    return this.pdfContainer.children[(field.position.z || 1) - 1] as HTMLElement;
  }

  private calcFieldAbsoluteLeftPosition(field: FloatingField, page: HTMLElement) {
    const positionX = (field.position.x * page.offsetWidth) / 100;
    const shellLeft = page.offsetLeft + positionX;

    return (shellLeft * 100) / this.pdfContainer.offsetWidth;
  }

  private calcFieldAbsoluteTopPosition(field: FloatingField, page: HTMLElement) {
    const positionY = (Math.max(field.position.y, 0) * page.offsetHeight) / 100;
    const shellTop = page.offsetTop + positionY;

    return (shellTop * 100) / this.pdfContainer.offsetHeight;
  }

  private fixFieldToInsideBoundaries(field: FloatingField, page?: HTMLElement) {
    page = page || this.currentPageFor(field);
    if (page) {
      const baseWidth = this.fieldPageWidth(field.element, page);
      const baseHeight = this.fieldPageHeight(field.element, page);
      const width = this.renderedFieldWidth(field, page);
      const height = this.renderedFieldHeight(field, page);
      const visualLeft = (field.position.x * page.offsetWidth) / 100 - (width - baseWidth) / 2;
      const visualTop = (field.position.y * page.offsetHeight) / 100 - (height - baseHeight) / 2;
      const clampedVisualLeft = Math.min(Math.max(visualLeft, (-9 * page.offsetWidth) / 100), page.offsetWidth - width + (5 * page.offsetWidth) / 100);
      const clampedVisualTop = Math.min(Math.max(visualTop, 0), page.offsetHeight - height);

      field.position.x = ((clampedVisualLeft + (width - baseWidth) / 2) * 100) / page.offsetWidth;
      field.position.y = ((clampedVisualTop + (height - baseHeight) / 2) * 100) / page.offsetHeight;
    }
    return field;
  }

  private fixFieldToCenterPosition(field: FloatingField) {
    const currentPage = this.currentPageFor(field);
    field.position.x = field.position.x - (this.fieldPageWidth(field.element, currentPage) * 100) / currentPage.offsetWidth / 2;
    field.position.y = field.position.y - (this.fieldPageHeight(field.element, currentPage) * 100) / currentPage.offsetHeight / 2;
    return field;
  }

  private fieldHeight(element: PositionElementEnum) {
    return [PositionElementEnum.Signature, PositionElementEnum.Initials].includes(element) ? this.returnFieldFullHeight(element) : this.fieldSizes.partialHeight;
  }

  private getBorderType(field: FloatingField): FloatingFieldBorderType {
    const certificate = field.signature?.certificate;
    if (!certificate) {
      return FloatingFieldBorderType.Advanced;
    }

    return this.certificateService.isIcpBrasilCertificate(certificate.payload) ? FloatingFieldBorderType.NationalCertificate : FloatingFieldBorderType.InternationalCertificate;
  }
}
